import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { scaledCoordinate } from '../drawing.helper'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { getTimeWidthInMilliSeconds } from '../../chartHelpers'
import { istToUTC, toDayFormat } from '../../../../util/dateTime'

export type ForecastDrawingState = {
    p1?: Point,
    p2?: Point
}

export type ForecastDrawingSettings = {
    fillColor: ColorValue,
    strokeColor: ColorValue,
    strokeWidth: number,
    labelColor: ColorValue
}


class ForecastDrawingTool extends DrawingBase<any, ForecastDrawingSettings, ForecastDrawingState> {

    days = 0
    hours = 0
    minutes = 0
    seconds = 0
    change = 0
    changePercentage = 0
    primaryRadius = 0
    secondaryRadius = 0

    scaledCoordinates: Record<string, PointCoordinates> = {
        p1: { x: 0, y: 0 },
        p2: { x: 0, y: 0 },
        center: { x: 0, y: 0 }
    }

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context

        this.calculateAttributes()
        const { center, p1, p2 } = this.scaledCoordinates

        ctx.beginPath()
        ctx.fillStyle = this._settings.fillColor.color
        ctx.globalAlpha = this._settings.fillColor.opacity
        ctx.ellipse(center.x, center.y, this.primaryRadius, this.secondaryRadius, 0, 0, p2.y > p1.y ? -Math.PI / 2 : Math.PI / 2, p2.y > p1.y ? true : false)
        ctx.globalAlpha = this._settings.strokeColor.opacity
        ctx.strokeStyle = this._settings.strokeColor.color
        ctx.lineWidth = this._settings.strokeWidth * window.devicePixelRatio
        ctx.stroke()
        ctx.closePath()

        this.drawStartStatsText(ctx)
        this.drawEndStatsText(ctx)

        ctx.beginPath()
        ctx.arc(p1.x, p1.y, 3 * window.devicePixelRatio, 0, 3 * Math.PI)
        ctx.fillStyle = '#646464'
        ctx.fill()

        ctx.beginPath()
        ctx.arc(p2.x, p2.y, 3 * window.devicePixelRatio, 0, 3 * Math.PI)
        ctx.fillStyle = '#646464'
        ctx.fill()
    };

    drawStartStatsText(ctx: CanvasRenderingContext2D) {

        const { p1, p2 } = this._state

        if (!p1 || !p2) return

        const { p1: p1s, p2: p2s } = this.scaledCoordinates

        const line1Text = p1.y.toFixed(2)
        const line2Text = this.formatTime(p1.x)

        ctx.globalAlpha = 1
        const fontSize = 12 * window.devicePixelRatio
        ctx.font = fontSize + 'px sans-serif'
        ctx.textBaseline = 'top'
        let dim1 = ctx.measureText(line1Text)
        const text1Height = dim1.fontBoundingBoxAscent + dim1.fontBoundingBoxDescent
        const text1Width = dim1.width

        let dim2 = ctx.measureText(line2Text)
        const text2Height = dim2.fontBoundingBoxAscent + dim2.fontBoundingBoxDescent
        const text2Width = dim2.width

        const padding = 5 * window.devicePixelRatio

        const rectWidth = Math.max(text1Width, text2Width)
        const startX = p1s.x - rectWidth / 2
        const start2Y = p1s.y + (p2s.y < p1s.y ? text1Height + 3 * padding : -text2Height - 2 * padding)
        const start1Y = p1s.y + (p2s.y < p1s.y ? 2 * padding : -text2Height - 3 * padding - text1Height)


        ctx.beginPath()
        ctx.fillStyle = this._settings.labelColor.color
        const rectHeight = text1Height + text2Height + 3 * padding
        ctx.roundRect(startX - padding, start1Y - padding, rectWidth + 2 * padding, rectHeight, 3)
        ctx.fill()
        ctx.closePath()


        this.drawToolTipArrow(ctx, p1s.x, p1s.y, p2s.y < p1s.y ? p1s.y + padding : p1s.y - padding)

        ctx.fillStyle = '#fff'
        ctx.fillText(line1Text, startX, start1Y)
        ctx.fillText(line2Text, startX, start2Y)
    }

    drawEndStatsText(ctx: CanvasRenderingContext2D) {

        const { p1, p2 } = this._state

        if (!p1 || !p2) return

        const { p1: p1s, p2: p2s } = this.scaledCoordinates


        let line1Text = `${this.change.toFixed(2)} (${this.changePercentage.toFixed(2)}%) in`

        if (this.days) {
            line1Text += ` ${this.days}d`
        }

        if (this.hours) {
            line1Text += ` ${this.hours}h`
        }

        if (this.minutes) {
            line1Text += ` ${this.minutes}m`
        }

        if (this.seconds) {
            line1Text += ` ${this.seconds}s`
        }
        const line2Text = `${p2.y.toFixed(2)} @ ${this.formatTime(p1.x)}`

        ctx.globalAlpha = 1
        const fontSize = 12 * window.devicePixelRatio
        ctx.font = fontSize + 'px sans-serif'
        ctx.textBaseline = 'top'
        let dim1 = ctx.measureText(line1Text)
        const text1Height = dim1.fontBoundingBoxAscent + dim1.fontBoundingBoxDescent
        const text1Width = dim1.width

        let dim2 = ctx.measureText(line2Text)
        const text2Height = dim2.fontBoundingBoxAscent + dim2.fontBoundingBoxDescent
        const text2Width = dim2.width

        const padding = 5 * window.devicePixelRatio

        let rectWidth = Math.max(text1Width, text2Width)
        const startX = p2s.x - rectWidth / 2
        const start2Y = p2s.y + (p2s.y > p1s.y ? text1Height + 3 * padding : -text2Height - 2 * padding)
        const start1Y = p2s.y + (p2s.y > p1s.y ? 2 * padding : -text2Height - 3 * padding - text1Height)

        ctx.beginPath()
        ctx.fillStyle = this._settings.labelColor.color
        let rectHeight = text1Height + text2Height + 3 * padding
        const rectStartX = startX - padding, rectStartY = start1Y - padding
        ctx.roundRect(rectStartX, rectStartY, rectWidth + 2 * padding, rectHeight, 3)
        ctx.fill()
        ctx.closePath()


        this.drawToolTipArrow(ctx, p2s.x, p2s.y, p2s.y > p1s.y ? p2s.y + padding : p2s.y - padding)

        ctx.fillStyle = '#fff'
        ctx.fillText(line1Text, startX, start1Y)
        ctx.fillText(line2Text, startX, start2Y)

        const index = this.timeToLogical(p2.x) || 0
        if (!index) return

        const data = this.getSeries().data()
        const candle = data[index]

        if (!candle) return

        const statusText = (p2.y >= p1.y && (candle.high || candle.value) >= p2.y) || (p2.y < p1.y && (candle.low || candle.value) <= p2.y) ? 'SUCCESS' : 'FAILURE'

        const dimStatus = ctx.measureText(statusText)
        const statusTextHeight = dimStatus.fontBoundingBoxAscent + dimStatus.fontBoundingBoxDescent
        const statusTextWidth = dimStatus.width

        rectWidth = statusTextWidth
        const statusStartX = p2s.x - rectWidth / 2

        const statusStartY = p2.y > p1.y ? rectStartY - padding - statusTextHeight : rectStartY + rectHeight + padding
        rectHeight = statusTextHeight + padding

        ctx.beginPath()
        ctx.fillStyle = statusText === 'SUCCESS' ? '#4caf50' : '#f23645'
        ctx.roundRect(statusStartX - padding, statusStartY - padding / 2, rectWidth + 2 * padding, rectHeight, 3)
        ctx.fill()
        ctx.closePath()

        ctx.fillStyle = '#fff'
        ctx.fillText(statusText, statusStartX, statusStartY)
    }

    drawToolTipArrow(ctx: CanvasRenderingContext2D, x: number, y: number, y2: number) {
        const height = Math.abs(y2 - y)
        const t1x = x - height
        const t2x = x + height

        ctx.beginPath()
        ctx.moveTo(x, y)
        ctx.lineTo(t1x, y2)
        ctx.lineTo(t2x, y2)
        ctx.lineTo(x, y)

        ctx.fillStyle = this._settings.labelColor.color
        ctx.fill()
        ctx.closePath()

    }


    calculateAttributes() {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return

        let p1x = this.timeToCoordinate(p1.x)
        let p2x = this.timeToCoordinate(p2.x)
        let p1y = this.priceToCoordinate(p1.y)
        let p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            this.scaledCoordinates.p1.x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p1.y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.p2.x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p2.y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
        }

        this.scaledCoordinates.center = {
            x: this.scaledCoordinates.p1.x,
            y: this.scaledCoordinates.p2.y
        }

        this.primaryRadius = this.scaledCoordinates.p2.x - this.scaledCoordinates.p1.x
        this.secondaryRadius = Math.abs(this.scaledCoordinates.p2.y - this.scaledCoordinates.p1.y)
        this.change = p2.y - p1.y
        this.changePercentage = this.change * 100 / p1.y

        const p1Index = this.timeToLogical(p1.x) || 0
        const p2Index = this.timeToLogical(p2.x) || 0

        const minIndex = Math.min(p1Index, p2Index), maxIndex = Math.max(p1Index, p2Index)

        const data = this.getSeries().data()
        const dataLength = data.length
        const emptySlots = this._chart.emptyTimeSlots
        let lastDateIndex: number = -1
        let lastDate: string = ''
        let numOfDays = -1
        for (let c = minIndex; c <= maxIndex; c++) {
            let candle = data[c] || emptySlots[c - dataLength]
            let currentDate = toDayFormat(istToUTC(candle.time as number) * 1000)
            if (lastDate !== currentDate) {
                lastDateIndex = c
                lastDate = currentDate
                numOfDays += 1
                continue
            }
        }
        const numOfBars = maxIndex - lastDateIndex + 1
        const timeBucket = this._chart.chartConfig.timeBucket

        this.days = numOfDays
        let timeWidth = numOfBars * getTimeWidthInMilliSeconds(timeBucket)

        this.hours = Math.floor(timeWidth / 3600000)
        timeWidth = timeWidth - this.hours * 3600000
        this.minutes = Math.floor(timeWidth / 60000)
        this.seconds = (timeWidth - this.minutes * 60000) / 1000

    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {

        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.p1 = point
        } else if (this.lastAddedPoint.id == 'p1') {
            point.id = 'p2'
            point.x = Math.max(this._state.p1!.x as number, time as number) as Time
            this._state.p2 = point
            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2 } = this._state

        p1 && handles.push(p1)
        p2 && handles.push(p2)

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const { p1, p2, center } = this.scaledCoordinates

        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio) - center.x
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio) - center.y

        if (scaledX < 0 || (p2.y > p1.y && scaledY > 0) || (p2.y < p1.y && scaledY < 0)) return null

        const d = Math.pow(scaledX, 2) / Math.pow(this.primaryRadius, 2) + Math.pow(scaledY, 2) / Math.pow(this.secondaryRadius, 2)
        if (d >= 0.9 && d <= 1.1) return {
            externalId: this.id,
            zOrder: 'normal'
        }

        return null
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {

        switch (id) {
            case 'p1':
                this._state.p1!.x = time
                this._state.p1!.y = price
                break
            case 'p2':
                this._state.p2!.x = Math.max(this._state.p1!.x as number, time as number) as Time
                this._state.p2!.y = price
                break
        }
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []
        return [
            {
                id: 'price',
                p1: Math.min(p1.y, p2.y),
                p2: Math.max(p1.y, p2.y),
                fill: true,
                fillColor: this._settings.labelColor.color,
                labelColors: {
                    p1: this._settings.labelColor.color,
                    p2: this._settings.labelColor.color
                }
            }
        ]

    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []

        const sorted = [p1, p2].sort((a, b) => (a.x as number) - (b.x as number))

        return [{
            id: 'time',
            p1: sorted[0].x,
            p2: sorted[1].x,
            fill: true,
            fillColor: this._settings.labelColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }


    getAvailableSettings = (): DrawingSettingGroup<ForecastDrawingSettings, SettingValues>[] => {
        return [
            {
                id: 'style',
                displayName: 'Style',
                visible: true,
                settings: [
                    {
                        name: 'fillColor',
                        label: 'Fill Color',
                        type: SettingType.COLOR,
                        defaultValue: {
                            color: '#c2deff',
                            opacity: 0.3
                        },
                        custom: true,
                        visible: true
                    },
                    {
                        name: 'labelColor',
                        label: 'Label Color',
                        type: SettingType.COLOR,
                        defaultValue: {
                            color: '#6397d4',
                            opacity: 0.7
                        },
                        custom: true,
                        visible: true
                    },
                    {
                        name: 'strokeColor',
                        label: 'Border Color',
                        type: SettingType.COLOR,
                        defaultValue: { color: '#3876EE', opacity: 0.5 },
                        custom: true,
                        visible: true
                    },
                    {
                        name: 'strokeWidth',
                        label: 'Border Width',
                        type: SettingType.STROKE_WIDTH,
                        defaultValue: 2,
                        custom: true,
                        visible: true
                    }
                ]
            }
        ]
    }

    getSettingsValues(): ForecastDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof ForecastDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = [...s.settings.map(setting => setting.name).filter(v => v !== 'labelColor')]
            }
        })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'forecast'
    }
}

export default ForecastDrawingTool